import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, FileText, Send, Download, CreditCard, Wallet, Video, QrCode,
  ShieldCheck, Sparkles, Check, ArrowRight, ExternalLink, Link2,
} from "lucide-react";
import CheckinBetaBanner from "../components/CheckinBetaBanner";

const CREAM = "#FFF8EC", INK = "#16130F", MUTED = "#6B6259", ACCENT = "#d04260", AMBER = "#F59E0B";

const FEATURES = [
  { icon: FileText, t: "Pre check-in nel Welcome Book", d: "L'ospite carica i documenti dal link, prima dell'arrivo. Niente app, niente carta: tutto dentro il Welcome Book più bello che esista." },
  { icon: Send, t: "Invio automatico ad Alloggiati Web e ROSS 1000", d: "Le schedine partono da sole verso la Questura e la Regione. Tu non devi più ricopiare nulla a mano." },
  { icon: Download, t: "Schedine scaricabili · giornaliero e mensile", d: "Ricevute e schedine pronte in PDF ogni giorno, più l'archivio mensile in un unico ZIP." },
  { icon: CreditCard, t: "Imposta di soggiorno con un click · Stripe", d: "L'ospite paga online con Apple Pay, Google Pay, PayPal o carta. L'incasso arriva sul tuo conto (Stripe applica piccole commissioni)." },
  { icon: Wallet, t: "…oppure in contanti", d: "Preferisci il contante? Invii lo stesso link e l'ospite trova le tue istruzioni. Sempre e solo dal Welcome Book." },
];

const Card = ({ children, style, ...p }) => (
  <div style={{ background: "#fff", border: "1px solid #F1E8D8", borderRadius: 24, ...style }} {...p}>{children}</div>
);

export default function PluginCheckinShowcase() {
  const navigate = useNavigate();
  return (
    <div style={{ background: CREAM, color: INK, minHeight: "100vh" }} data-testid="plugin-checkin-showcase">
      <style>{"@keyframes pcsUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}.pcs-up{animation:pcsUp .6s ease both}"}</style>

      {/* Top bar */}
      <div className="sticky top-0 z-20 backdrop-blur" style={{ background: "rgba(255,248,236,.82)", borderBottom: "1px solid #F1E8D8" }}>
        <div className="max-w-4xl mx-auto px-5 py-3 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-2 text-sm font-bold" style={{ color: MUTED }} data-testid="pcs-back">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <span className="text-[11px] font-black uppercase tracking-[0.16em]" style={{ color: ACCENT }}>Hostland · Check-in</span>
        </div>
      </div>

      {/* HERO */}
      <section className="px-5 pt-8 pb-10">
        <div className="max-w-4xl mx-auto rounded-[32px] relative overflow-hidden pcs-up" style={{ background: "linear-gradient(145deg,#1a1210 0%,#130d1c 55%,#0d1322 100%)" }}>
          <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 15% 20%,white 1px,transparent 1px),radial-gradient(circle at 82% 68%,white 1px,transparent 1px)", backgroundSize: "46px 46px" }} />
          <div aria-hidden className="absolute -top-28 -left-20 w-80 h-80 rounded-full blur-3xl opacity-30 pointer-events-none" style={{ background: `radial-gradient(circle,${ACCENT},transparent 70%)` }} />
          <div aria-hidden className="absolute -bottom-28 -right-16 w-80 h-80 rounded-full blur-3xl opacity-25 pointer-events-none" style={{ background: `radial-gradient(circle,${AMBER},transparent 70%)` }} />
          <div className="relative z-10 p-8 md:p-12 text-center">
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em]" style={{ background: `${AMBER}1f`, border: `1px solid ${AMBER}44`, color: "#FCD572" }}>
              <Sparkles className="w-3.5 h-3.5" /> Plugin registrazione documenti
            </span>
            <h1 className="text-white font-black tracking-tighter mt-4 leading-[1.03] text-4xl md:text-5xl">
              Il check-in più pazzesco<br />che esista
            </h1>
            <p className="text-zinc-300 text-sm md:text-base font-medium mt-4 max-w-2xl mx-auto">
              Pre check-in, invio automatico alla Questura, schedine pronte e imposta di soggiorno pagabile con un click. Tutto integrato nel tuo Welcome Book: basta inviare un link.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
              {["Apple Pay", "Google Pay", "PayPal", "Carta"].map((p) => (
                <span key={p} className="rounded-full bg-white text-zinc-900 text-[11px] font-black px-3 py-1.5">{p}</span>
              ))}
            </div>
            <p className="text-[11px] text-zinc-500 mt-3">Imposta di soggiorno: Stripe applica piccole commissioni sui pagamenti online.</p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="px-5 pb-6">
        <div className="max-w-4xl mx-auto grid sm:grid-cols-2 gap-3">
          {FEATURES.map((f, i) => (
            <Card key={i} className="pcs-up" style={{ padding: 18, animationDelay: `${i * 70}ms` }} data-testid={`pcs-feat-${i}`}>
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${ACCENT}1a`, color: ACCENT }}><f.icon className="w-5 h-5" /></span>
                <div>
                  <div className="font-black text-[15px] leading-snug">{f.t}</div>
                  <div className="text-[13px] font-medium mt-1 leading-relaxed" style={{ color: MUTED }}>{f.d}</div>
                </div>
              </div>
            </Card>
          ))}
          <Card className="pcs-up flex items-center gap-3" style={{ padding: 18, background: "#111", borderColor: "#111", color: "#fff", animationDelay: "350ms" }}>
            <span className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 bg-white/15"><Link2 className="w-5 h-5" /></span>
            <div>
              <div className="font-black text-[15px] leading-snug">Un solo link, tutto risolto</div>
              <div className="text-[13px] font-medium mt-1 leading-relaxed text-white/70">Invii il link del Welcome Book: documenti, check-in e imposta in un unico posto.</div>
            </div>
          </Card>
        </div>
      </section>

      {/* DEVISU ADD-ON */}
      <section className="px-5 py-6">
        <div className="max-w-4xl mx-auto rounded-[28px] relative overflow-hidden pcs-up" style={{ background: "linear-gradient(135deg,#0A1222 0%,#101a30 60%,#0A1222 100%)" }}>
          <div aria-hidden className="absolute -top-20 -right-16 w-72 h-72 rounded-full blur-3xl opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle,#2F6FE0,transparent 70%)" }} />
          <div className="relative z-10 p-8 md:p-10">
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em]" style={{ background: "#2F6FE01f", border: "1px solid #2F6FE044", color: "#93B4F5" }}>
              <Video className="w-3.5 h-3.5" /> Plugin aggiuntivo · DeVisu
            </span>
            <h2 className="text-white font-black tracking-tight mt-4 text-2xl md:text-3xl leading-tight">Identificazione da remoto, a norma</h2>
            <p className="text-zinc-300 text-sm font-medium mt-3 max-w-2xl">
              QR code sulla porta e videochiamata con un tocco dal Welcome Book: riconosci l'ospite a distanza come previsto dalla <b className="text-white">sentenza del Consiglio di Stato</b> sull'identificazione remota.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-5">
              <span className="inline-flex items-center gap-2 text-[13px] font-bold text-zinc-200"><QrCode className="w-4 h-4" style={{ color: "#93B4F5" }} /> QR sulla porta</span>
              <span className="inline-flex items-center gap-2 text-[13px] font-bold text-zinc-200"><Video className="w-4 h-4" style={{ color: "#93B4F5" }} /> Videochiamata</span>
              <span className="inline-flex items-center gap-2 text-[13px] font-bold text-zinc-200"><ShieldCheck className="w-4 h-4" style={{ color: "#93B4F5" }} /> Conforme alla sentenza</span>
            </div>
            <button onClick={() => navigate("/dashboard/devisu")} data-testid="pcs-devisu-link"
              className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-3 font-black text-white" style={{ background: "linear-gradient(135deg,#2F6FE0,#1E40AF)" }}>
              Apri DeVisu <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* BETA ACCESS */}
      <section className="px-5 pb-14">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-4 pcs-up">
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em]" style={{ background: "#EAFBF0", color: "#047857", border: "1px solid #A7E3BC" }}>
              <Check className="w-3.5 h-3.5" /> Beta privata aperta
            </span>
            <h2 className="font-black tracking-tight mt-3 text-2xl md:text-3xl">Entra ora nella beta privata</h2>
            <p className="text-sm font-medium mt-2 max-w-2xl mx-auto" style={{ color: MUTED }}>
              Durante la beta lo usi in anteprima. Al termine della beta pubblica diventerà acquistabile a <b style={{ color: INK }}>19,90 € l'anno</b> per <b style={{ color: INK }}>tutte le strutture</b> del tuo account: lo sblocco è unico.
            </p>
          </div>
          <CheckinBetaBanner forceShow />
        </div>
      </section>
    </div>
  );
}
